import { yupResolver } from '@hookform/resolvers/yup';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import * as yup from 'yup';

import { useAuth } from '../../context/AuthContext';
import { AuthError } from '../../types/auth';

// Esquema de validación
const loginSchema = yup.object({
  email: yup
    .string()
    .required('El correo electrónico es obligatorio')
    .email('Ingrese un correo electrónico válido'),
  password: yup
    .string()
    .required('La contraseña es obligatoria')
    .min(6, 'La contraseña debe tener al menos 6 caracteres'),
});

// Tipo para los datos del formulario
type LoginFormData = yup.InferType<typeof loginSchema>;

interface LoginFormProps {
  redirectUrl?: string;
}

const LoginForm: React.FC<LoginFormProps> = ({ redirectUrl = '/dashboard' }) => {
  const { login, loginWithGoogle, error: authError } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<AuthError | null>(null);
  const navigate = useNavigate();

  // Configurar react-hook-form con validación de yup
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: yupResolver(loginSchema),
  });

  // Manejar errores de autenticación
  const handleError = (error: unknown) => {
    const errorCode = (error as Error & { code?: string })?.code || 'auth/unknown';
    let errorMessage = '';

    switch (errorCode) {
      case 'auth/user-not-found':
      case 'auth/wrong-password':
        errorMessage = 'Correo electrónico o contraseña incorrectos';
        break;
      case 'auth/too-many-requests':
        errorMessage = 'Demasiados intentos fallidos. Inténtalo más tarde';
        break;
      case 'auth/network-request-failed':
        errorMessage = 'Error de red. Comprueba tu conexión';
        break;
      default:
        errorMessage = (error as Error)?.message || 'Ha ocurrido un error desconocido';
    }

    setError({
      code: errorCode,
      message: errorMessage,
    });
  };

  // Enviar formulario
  const onSubmit = async (data: LoginFormData) => {
    try {
      setIsLoading(true);
      setError(null);
      await login(data.email, data.password);
      navigate(redirectUrl); // Redirigir a la URL especificada después del login
    } catch (err) {
      handleError(err);
    } finally {
      setIsLoading(false);
    }
  };

  // Iniciar sesión con Google
  const handleGoogleLogin = async () => {
    try {
      setIsLoading(true);
      setError(null);
      await loginWithGoogle();
      navigate(redirectUrl); // Redirigir a la URL especificada después del login
    } catch (err) {
      handleError(err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="card">
        <h2 className="text-2xl font-bold text-center text-gray-800 mb-8">
          Iniciar sesión en NFCores
        </h2>

        {/* Mostrar error si existe */}
        {(error || authError) && (
          <div className="bg-alert-50 border border-alert-200 text-alert-700 px-4 py-3 rounded-lg mb-4">
            <p>{error?.message || authError?.message}</p>
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Campo de correo electrónico */}
          <div>
            <label htmlFor="email" className="form-label">
              Correo electrónico
            </label>
            <input
              id="email"
              type="email"
              className={`form-input ${errors.email ? 'border-alert-500' : ''}`}
              {...register('email')}
              disabled={isLoading}
            />
            {errors.email && <p className="form-error">{errors.email.message}</p>}
          </div>

          {/* Campo de contraseña */}
          <div>
            <label htmlFor="password" className="form-label">
              Contraseña
            </label>
            <input
              id="password"
              type="password"
              className={`form-input ${errors.password ? 'border-alert-500' : ''}`}
              {...register('password')}
              disabled={isLoading}
            />
            {errors.password && <p className="form-error">{errors.password.message}</p>}
          </div>

          {/* Enlace para olvidó contraseña */}
          <div className="text-right">
            <Link to="/auth/reset-password" className="text-sm text-primary-600 hover:text-primary-700">
              ¿Olvidaste tu contraseña?
            </Link>
          </div>

          {/* Botón de inicio de sesión */}
          <button
            type="submit"
            className="btn-primary w-full"
            disabled={isLoading}
          >
            {isLoading ? 'Iniciando sesión...' : 'Iniciar sesión'}
          </button>
        </form>

        {/* Separador */}
        <div className="relative flex items-center justify-center mt-6 mb-6">
          <div className="border-t border-gray-300 absolute w-full"></div>
          <span className="bg-white px-3 text-gray-500 text-sm relative">o</span>
        </div>

        {/* Botón de Google */}
        <button
          type="button"
          onClick={handleGoogleLogin}
          className="flex items-center justify-center w-full border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 px-4 py-2 rounded-lg font-medium"
          disabled={isLoading}
        >
          <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
            <path
              fill="#4285F4"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="#34A853"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="#FBBC05"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            />
            <path
              fill="#EA4335"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
          </svg>
          Continuar con Google
        </button>

        {/* Enlace para registrarse */}
        <p className="text-center mt-6 text-gray-600">
          ¿No tienes una cuenta?{' '}
          <Link to="/auth/register" className="text-primary-600 hover:text-primary-700 font-medium">
            Regístrate
          </Link>
        </p>
      </div>
    </div>
  );
};

export default LoginForm;
